import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/AdminLayout';

// Styled components
const StyledModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  h2 { margin: 0 0 1.5rem; font-size: 1.8rem; color: #333; }
  form { display: flex; flex-direction: column; gap: 1.2rem; }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  label { font-weight: 500; color: #444; font-size: 0.95rem; }
  select, input[type="text"], textarea {
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s;
    &:focus { outline: none; border-color: #2563eb; }
  }
  textarea {
    min-height: 80px;
    resize: vertical;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  button {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    &:first-child { background: #f3f4f6; border: none; color: #4b5563; &:hover { background: #e5e7eb; } }
    &:last-child { background: #2563eb; border: none; color: white; &:hover { background: #1d4ed8; } }
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  &.active {
    background: #dcfce7;
    color: #166534;
  }
  &.inactive {
    background: #fee2e2;
    color: #991b1b;
  }
`;

const HWIDCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(45, 108, 255, 0.11);
  border: 1px solid #e5e7eb;
  
  .hwid-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    .hwid-short {
      font-family: monospace;
      font-size: 1.1rem;
      font-weight: 600;
      color: #2563eb;
    }
  }
  
  .hwid-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    
    .detail-item {
      .label {
        font-size: 0.875rem;
        color: #6b7280;
        margin-bottom: 0.25rem;
      }
      .value {
        font-weight: 500;
        color: #374151;
      }
    }
  }
  
  .hwid-actions {
    margin-top: 1rem;
    display: flex;
    gap: 0.5rem;
    
    button {
      padding: 0.5rem 1rem;
      border: none;
      border-radius: 6px;
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.2s;
      
      &.verify {
        background: #059669;
        color: white;
        &:hover { background: #047857; }
      }
      
      &.delete {
        background: #dc2626;
        color: white;
        &:hover { background: #b91c1c; }
      }
    }
  }
`;

const HWIDPage = () => {
  const [hwids, setHwids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  
  const [newHWID, setNewHWID] = useState({
    hwid: '',
    username: '',
    license_type: 'standard',
    notes: ''
  });

  const [verifyHWID, setVerifyHWID] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    fetchHWIDs();
  }, []);

  const fetchHWIDs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/hwid/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setHwids(response.data.hwids || []);
      } else {
        setError(response.data.message || 'Failed to fetch HWIDs');
      }
    } catch (error) {
      setError(`Failed to fetch HWIDs: ${error.response?.data?.message || error.message}`);
    }
    setLoading(false);
  };

  const handleAddHWID = async (e) => {
    e.preventDefault();
    
    if (newHWID.hwid.length !== 64) {
      toast.error('Invalid HWID format (should be 64 characters)');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/hwid/add', {
        hwid: newHWID.hwid,
        username: newHWID.username || null,
        license_type: newHWID.license_type,
        notes: newHWID.notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`HWID ${newHWID.hwid.substring(0, 16)}... added successfully!`);
        setShowAddModal(false);
        setNewHWID({ hwid: '', username: '', license_type: 'standard', notes: '' });
        fetchHWIDs();
      } else {
        toast.error(response.data.message || 'Failed to add HWID');
      }
    } catch (error) {
      toast.error(`API Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleVerifyHWID = async (hwid = null) => {
    const hwidToVerify = hwid || verifyHWID;
    
    if (hwidToVerify.length !== 64) {
      toast.error('Invalid HWID format (should be 64 characters)');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/hwid/verify', {
        hwid: hwidToVerify,
        fingerprint: 'admin_check'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const result = {
          success: true,
          data: response.data.license_info || {}
        };
        setVerifyResult(result);
        
        if (!hwid) {
          setShowVerifyModal(true);
        }
        
        toast.success('HWID is authorized!');
        return result;
      } else {
        const result = {
          success: false,
          message: response.data.message || 'Not authorized'
        };
        setVerifyResult(result);
        
        if (!hwid) {
          setShowVerifyModal(true);
        }
        
        toast.error(`HWID verification failed: ${result.message}`);
        return result;
      }
    } catch (error) {
      const result = {
        success: false,
        message: error.response?.data?.message || error.message
      };
      setVerifyResult(result);
      
      if (!hwid) {
        setShowVerifyModal(true);
      }
      
      toast.error(`API Error: ${result.message}`);
      return result;
    }
  };

  const handleDeleteHWID = async (hwid) => {
    if (!window.confirm('Are you sure you want to delete this HWID? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`/api/hwid/${hwid.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('HWID deleted successfully');
        fetchHWIDs();
      } else {
        toast.error(response.data.message || 'Failed to delete HWID');
      }
    } catch (error) {
      toast.error(`API Error: ${error.response?.data?.message || error.message}`);
    }
  };

  if (loading) {
    return <AdminLayout><div className="loading">Loading...</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <section>
        <div className="section-header">
          <h2>HWID Management</h2>
          <button onClick={() => setShowAddModal(true)} className="action-button">Add HWID</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
          <button onClick={fetchHWIDs} disabled={loading} className="action-button">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button onClick={() => setShowVerifyModal(true)} className="action-button">
            Verify HWID
          </button>
        </div>

        {hwids.length > 0 ? (
          <div>
            <h3>HWIDs ({hwids.length})</h3>
            {hwids.map((hwid, index) => (
              <HWIDCard key={hwid.id || index}>
                <div className="hwid-header">
                  <div className="hwid-short">{hwid.hwid_short}</div>
                  <StatusBadge className={hwid.is_active ? 'active' : 'inactive'}>
                    {hwid.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </StatusBadge>
                </div>
                
                <div className="hwid-details">
                  <div className="detail-item">
                    <div className="label">Username</div>
                    <div className="value">{hwid.username || 'Unknown'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">License Type</div>
                    <div className="value">{hwid.license_type || 'Standard'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">First Seen</div>
                    <div className="value">{hwid.first_seen ? new Date(hwid.first_seen).toLocaleDateString() : 'Unknown'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Last Seen</div>
                    <div className="value">{hwid.last_seen ? new Date(hwid.last_seen).toLocaleDateString() : 'Unknown'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="label">Access Count</div>
                    <div className="value">{hwid.access_count || 0}</div>
                  </div>
                  {hwid.notes && (
                    <div className="detail-item">
                      <div className="label">Notes</div>
                      <div className="value">{hwid.notes}</div>
                    </div>
                  )}
                </div>
                
                <div className="hwid-actions">
                  <button 
                    className="verify" 
                    onClick={() => handleVerifyHWID(hwid.hwid)}
                  >
                    Verify
                  </button>
                  <button 
                    className="delete" 
                    onClick={() => handleDeleteHWID(hwid)}
                  >
                    Delete
                  </button>
                </div>
              </HWIDCard>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            No HWIDs found. Click "Add HWID" to create one.
          </div>
        )}

        {showAddModal && (
          <StyledModal onClick={(e) => e.target === e.currentTarget && setShowAddModal(false)}>
            <ModalContent>
              <h2>Add New HWID</h2>
              <form onSubmit={handleAddHWID}>
                <FormGroup>
                  <label>HWID (64 characters)*</label>
                  <input
                    type="text"
                    value={newHWID.hwid}
                    onChange={(e) => setNewHWID({...newHWID, hwid: e.target.value})}
                    placeholder="Enter 64-character HWID"
                    maxLength={64}
                    required
                  />
                  <small style={{ color: '#6b7280' }}>
                    Current length: {newHWID.hwid.length}/64
                  </small>
                </FormGroup>
                
                <FormGroup>
                  <label>Username (optional)</label>
                  <input
                    type="text"
                    value={newHWID.username}
                    onChange={(e) => setNewHWID({...newHWID, username: e.target.value})}
                    placeholder="Enter username"
                  />
                </FormGroup>
                
                <FormGroup>
                  <label>License Type</label>
                  <select
                    value={newHWID.license_type}
                    onChange={(e) => setNewHWID({...newHWID, license_type: e.target.value})}
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </FormGroup>
                
                <FormGroup>
                  <label>Notes (optional)</label>
                  <textarea
                    value={newHWID.notes}
                    onChange={(e) => setNewHWID({...newHWID, notes: e.target.value})}
                    placeholder="Enter any notes about this HWID"
                  />
                </FormGroup>
                
                <ModalActions>
                  <button type="button" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit">
                    Add HWID
                  </button>
                </ModalActions>
              </form>
            </ModalContent>
          </StyledModal>
        )}

        {showVerifyModal && (
          <StyledModal onClick={(e) => e.target === e.currentTarget && setShowVerifyModal(false)}>
            <ModalContent>
              <h2>Verify HWID</h2>
              <FormGroup>
                <label>HWID (64 characters)*</label>
                <input
                  type="text"
                  value={verifyHWID}
                  onChange={(e) => setVerifyHWID(e.target.value)}
                  placeholder="Enter 64-character HWID to verify"
                  maxLength={64}
                />
                <small style={{ color: '#6b7280' }}>
                  Current length: {verifyHWID.length}/64
                </small>
              </FormGroup>
              
              {verifyResult && (
                <div style={{ 
                  padding: '1rem', 
                  borderRadius: '8px',
                  background: verifyResult.success ? '#dcfce7' : '#fee2e2',
                  border: `1px solid ${verifyResult.success ? '#bbf7d0' : '#fecaca'}`,
                  color: verifyResult.success ? '#166534' : '#991b1b',
                  marginTop: '1rem'
                }}>
                  {verifyResult.success ? (
                    <div>
                      <strong>✅ HWID is authorized!</strong>
                      <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                        <div>User: {verifyResult.data.username || 'Unknown'}</div>
                        <div>License: {verifyResult.data.license_type || 'Unknown'}</div>
                        <div>Access Count: {verifyResult.data.access_count || 0}</div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <strong>❌ HWID verification failed</strong>
                      <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                        {verifyResult.message}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <ModalActions>
                <button type="button" onClick={() => {
                  setShowVerifyModal(false);
                  setVerifyResult(null);
                  setVerifyHWID('');
                }}>
                  Close
                </button>
                <button 
                  type="button" 
                  onClick={() => handleVerifyHWID()}
                  disabled={verifyHWID.length !== 64}
                >
                  Verify
                </button>
              </ModalActions>
            </ModalContent>
          </StyledModal>
        )}
      </section>
    </AdminLayout>
  );
};

export default HWIDPage; 